import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, onValue, push } from "firebase/database";
import { db } from "../firebaseConfig";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CTA from "./CTA";
import jsPDF from "jspdf";
import logo from "../assets/logo.jpg";
import logo1 from "../assets/image1.jpg";
import SEO from "./SEO";

const AllPackages = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleTours, setVisibleTours] = useState(8);
  const [showAllTours, setShowAllTours] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [enquiryFormData, setEnquiryFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    packageId: "",
    packageName: "",
    destination: "",
    duration: "",
    date: new Date().toISOString(),
    adults: 1,
    children: 0,
    travelDate: "",
    specialRequirements: "",
    hotelPreference: "",
    budget: "",
    howDidYouHear: "",
    emergencyContact: "",
    departureCity: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const inputRefs = useRef({});

  // Fetch packages from Firebase
  useEffect(() => {
    const database = getDatabase();
    const packagesRef = ref(database, "packages");

    const unsubscribe = onValue(packagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        let allPackages = [];
        Object.keys(data).forEach((country) => {
          const countryPackages = data[country];
          Object.keys(countryPackages).forEach((packageKey) => {
            const pkg = countryPackages[packageKey];
            let parsedItinerary = [];
            if (pkg.itinerary) {
              parsedItinerary = Object.values(pkg.itinerary);
            }
            let images = [];
            if (pkg.images) {
              if (Array.isArray(pkg.images)) {
                images = pkg.images;
              } else {
                images = Object.values(pkg.images);
              }
            }
            
            // Process price information
            let priceValues = [];
            let originalPriceValues = {};
            if (pkg.priceValues) {
              if (Array.isArray(pkg.priceValues)) {
                priceValues = pkg.priceValues;
              } else {
                priceValues = Object.values(pkg.priceValues);
                originalPriceValues = pkg.priceValues;
              }
            }
            
            // Format price display for card - Display the entire price line
            let cardPriceDisplay = "Price on request";
            
            // First try to get the first price from priceValues
            if (priceValues.length > 0) {
              // Always use the first price value and display the full line
              const firstPrice = priceValues[0];
              if (typeof firstPrice === 'string') {
                // For detailed price descriptions like "2 Adults: Total Price (INR): 49,900 /-* (exc. GST)"
                // Display the entire string as it contains important information
                cardPriceDisplay = firstPrice;
                
                // Replace INR/Inr with ₹ symbol for better display
                cardPriceDisplay = cardPriceDisplay.replace(/\b(INR|Inr)\.?/gi, "₹");
              } else if (typeof firstPrice === 'number') {
                cardPriceDisplay = `₹${firstPrice.toLocaleString('en-IN')}`;
              }
            } else if (pkg.price) {
              // Fallback to the price field if priceValues is not available
              if (typeof pkg.price === 'string') {
                // For detailed price descriptions
                cardPriceDisplay = pkg.price;
                cardPriceDisplay = cardPriceDisplay.replace(/\b(INR|Inr)\.?/gi, "₹");
              } else if (typeof pkg.price === 'number') {
                cardPriceDisplay = `₹${pkg.price.toLocaleString('en-IN')}`;
              }
            }
            
            // Check if the price already includes "per person" text
            const hasPerPersonInPrice = cardPriceDisplay.toLowerCase().includes("") || 
                                      cardPriceDisplay.toLowerCase().includes("per adult") ||
                                      cardPriceDisplay.toLowerCase().includes("per pax");
            
            const formattedPackage = {
              id: pkg.id || packageKey,
              name: pkg.name,
              packageId: pkg.packageId || packageKey,
              destination: pkg.destination || country,
              duration: pkg.duration || "Not specified",
              description: pkg.description || "",
              cardPriceDisplay: cardPriceDisplay,
              priceType: hasPerPersonInPrice ? "" : (pkg.priceType || "per person"),
              rawPrice: pkg.price,
              priceValues: priceValues,
              originalPriceValues: originalPriceValues,
              type: pkg.type || "international",
              includes: pkg.includes ? Object.values(pkg.includes) : [],
              excludes: pkg.excludes ? Object.values(pkg.excludes) : [],
              itinerary: parsedItinerary,
              images: images,
              image: images.length > 0 ? images[0] : null,
              color: Math.random() > 0.5 ? "#2563eb" : "#f59e42",
              nights: parseInt(pkg.duration?.match(/\d+N/)?.[0]) || 4,
              bestSelling: pkg.bestSelling || false,
              totalPrice: pkg.totalPrice || null,
              termsAndConditions: pkg.termsAndConditions || "",
            };
            allPackages.push(formattedPackage);
          });
        });

        setTours(allPackages);
      } else {
        setTours([]);
      }
      setLoading(false);
    });

    return () => typeof unsubscribe === "function" && unsubscribe();
  }, []);

  const handlePackageClick = (tour) => {
    setSelectedPackage(tour);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPackage(null);
  };

  const toggleToursVisibility = () => {
    if (showAllTours) {
      setVisibleTours(8);
    } else {
      setVisibleTours(tours.length);
    }
    setShowAllTours(!showAllTours);
  };

  const handleEnquireNow = useCallback((pkg) => {
    setEnquiryFormData({
      name: "",
      email: "",
      phone: "",
      message: "",
      packageId: pkg.id || "",
      packageName: pkg.title || "",
      destination: pkg.destination || "",
      duration: pkg.duration || "",
      date: new Date().toISOString(),
      adults: 1,
      children: 0,
      travelDate: "",
      specialRequirements: "",
      hotelPreference: "",
      budget: "",
      howDidYouHear: "",
      emergencyContact: "",
      departureCity: ""
    });
    setShowEnquiryModal(false);
    document.querySelector('.floating-enquire')?.click();
  }, []);

  const closeEnquiryModal = () => {
    setShowEnquiryModal(false);
  };

  const handleEnquiryChange = useCallback((e) => {
    const { name, value } = e.target;
    setEnquiryFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleEnquirySubmit = async (e) => {
    console.log("Submitting enquiry:", enquiryFormData);
    
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const database = getDatabase();
      const enquiriesRef = ref(database, "enquiry");
      await push(enquiriesRef, enquiryFormData);
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      alert("There was an error submitting your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };


  const generatePDF = async () => {
    const packageDetails = selectedPackage;
    if (!packageDetails) return;

    const PAGE_WIDTH = 210; // A4 width in mm
    const LEFT_MARGIN = 15;
    const RIGHT_MARGIN = 15;
    const TOP_MARGIN = 20;
    const BOTTOM_MARGIN = 275;
    const CONTENT_WIDTH = PAGE_WIDTH - LEFT_MARGIN - RIGHT_MARGIN;

    let yPosition = 10;

    // Load logo
    let dataURL = '';
    if (typeof logo === "string" && logo.startsWith("data:")) {
      dataURL = logo;
    } else {
      const img = new window.Image();
      img.src = logo1;
      await new Promise((resolve) => (img.onload = resolve));
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      dataURL = canvas.toDataURL("image1/jpg");
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Set default font for text measurements
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    // LOGO
    const logoWidth = 200;
    const logoHeight = 100;
    const centerX = 105 - logoWidth / 2;
    doc.addImage(dataURL, "PNG", centerX, yPosition, logoWidth, logoHeight);
    yPosition += logoHeight + 5;

    // HEADER
    // doc.setFontSize(22);
    // doc.setTextColor(40, 40, 40);
    // doc.setFont("helvetica", "bold");
    // doc.text("Pearl Tours & Travels", 105, yPosition, { align: "center" });
    // yPosition += 10;
    // doc.setFontSize(10);
    // doc.setTextColor(100, 100, 100);
    // doc.setFont("helvetica", "normal");
    // doc.text("Address: Pearl Enclave, MSEB Rd, next to Chai Sutta Bar,", 105, yPosition, { align: "center" });
    // yPosition += 5;
    // doc.text("Viya Nagar, Vishrambag, Sangli, Maharashtra 416415", 105, yPosition, { align: "center" });
    // yPosition += 5;
    // doc.text("Mobile: +91-73034 55505 | Email: pearltoursandtravelsangli@gmail.com", 105, yPosition, { align: "center" });
    // yPosition += 15;

    // doc.setFontSize(16);
    // doc.setTextColor(30, 58, 138);
    // doc.setFont("helvetica", "bold");
    // doc.text("TOUR PACKAGE DETAILS", 105, yPosition, { align: "center" });
    // yPosition += 15;

    // doc.setDrawColor(200, 200, 200);
    // doc.setLineWidth(0.5);
    // doc.line(LEFT_MARGIN, yPosition, PAGE_WIDTH - LEFT_MARGIN, yPosition);
    // yPosition += 10;

    // Package name
    doc.setFontSize(14);
    doc.setTextColor(30, 58, 138);
    doc.setFont("helvetica", "bold");
    
    // Use jsPDF's built-in text splitting for package name
    const packageNameLines = doc.splitTextToSize(packageDetails.name.toUpperCase(), CONTENT_WIDTH);
    packageNameLines.forEach((line, index) => {
      doc.text(line, LEFT_MARGIN, yPosition + (index * 7));
    });
    yPosition += packageNameLines.length * 7 + 5;

    doc.setFontSize(12);
    doc.setTextColor(80, 80, 80);
    doc.setFont("helvetica", "normal");
    doc.text(`Destination: ${packageDetails.destination}`, LEFT_MARGIN, yPosition);
    yPosition += 7;
    doc.text(`Duration: ${packageDetails.duration}`, LEFT_MARGIN, yPosition);
    yPosition += 7;

    // ========================
    // PRICE DETAILS
    // ========================
    doc.setFontSize(12);
    doc.setTextColor(30, 58, 138);
    doc.setFont("helvetica", "bold");
    doc.text("PRICE DETAILS", LEFT_MARGIN, yPosition);
    yPosition += 7;

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    if (
      packageDetails.priceValues &&
      Array.isArray(packageDetails.priceValues) &&
      packageDetails.priceValues.length > 0
    ) {
      doc.setFillColor(241, 243, 245);
      doc.rect(LEFT_MARGIN - 5, yPosition, CONTENT_WIDTH + 10, 8, "F");
      doc.setFontSize(10);
      doc.setTextColor(30, 58, 138);
      doc.text("Price Breakdown", LEFT_MARGIN, yPosition + 6);
      yPosition += 15;

      packageDetails.priceValues.forEach((priceValue, index) => {
        let displayText = priceValue;
        if (typeof priceValue === "string") {
          // Format the price text for better readability
          displayText = priceValue.replace(/\b(INR|Inr)\./gi, "₹");
        }

        // Use jsPDF's built-in text splitting for price values
        const priceLines = doc.splitTextToSize(displayText, CONTENT_WIDTH);

        priceLines.forEach((line, index) => {
          if (index === 0) {
            doc.text(line, LEFT_MARGIN, yPosition);
          } else {
            yPosition += 6;
            doc.text(line, LEFT_MARGIN, yPosition);
          }
        });
        yPosition += 8;

        if (yPosition < BOTTOM_MARGIN - 10) {
          doc.setDrawColor(220, 220, 220);
          doc.setLineWidth(0.2);
          doc.line(LEFT_MARGIN + 5, yPosition, PAGE_WIDTH - LEFT_MARGIN, yPosition);
          yPosition += 5;
        }
      });

      if (packageDetails.totalPrice) {
        doc.setFont("helvetica", "bold");
        doc.text("Total Package Price:", LEFT_MARGIN, yPosition);
        const totalFormatted = `₹${Number(packageDetails.totalPrice) || 0}`;
        doc.text(totalFormatted, LEFT_MARGIN + 55, yPosition);
        yPosition += 10;
      }
    } else if (packageDetails.rawPrice) {
      let displayText = packageDetails.rawPrice;
      if (typeof packageDetails.rawPrice === "string") {
        displayText = packageDetails.rawPrice.replace(/\b(INR|Inr)\./gi, "₹");
      }
      doc.text(`Price: ${displayText}`, LEFT_MARGIN, yPosition);
      yPosition += 7;
    } else {
      doc.text("No pricing information available", LEFT_MARGIN, yPosition);
      yPosition += 7;
    }
    yPosition += 10;

    // DESCRIPTION
    doc.addPage();
    yPosition = TOP_MARGIN;
    doc.setFontSize(14);
    doc.setTextColor(30, 58, 138);
    doc.setFont("helvetica", "bold");
    doc.text("DESCRIPTION", LEFT_MARGIN, yPosition);
    yPosition += 10;
    doc.setFontSize(11);
    doc.setTextColor(50, 50, 50);
    
    // Use jsPDF's built-in text splitting for description
    const descriptionLines = doc.splitTextToSize(packageDetails.description || "No description available", CONTENT_WIDTH);
    for (let i = 0; i < descriptionLines.length; i++) {
      if (yPosition + 6 > BOTTOM_MARGIN) {
        doc.addPage();
        yPosition = TOP_MARGIN;
      }
      doc.text(descriptionLines[i], LEFT_MARGIN, yPosition);
      yPosition += 6;
    }
    yPosition += 10;

    // ITINERARY
    if (packageDetails.itinerary && Array.isArray(packageDetails.itinerary) && packageDetails.itinerary.length > 0) {
      doc.addPage();
      yPosition = TOP_MARGIN;
      doc.setFontSize(16);
      doc.setTextColor(30, 58, 138);
      doc.setFont("helvetica", "bold");
      doc.text("DETAILED ITINERARY", 105, yPosition, { align: "center" });
      yPosition += 15;

      packageDetails.itinerary.forEach((description, index) => {
        const dayTitle = `Day ${index + 1}`;
        
        // Check if we need a new page before adding day title
        if (yPosition + 20 > BOTTOM_MARGIN) {
          doc.addPage();
          yPosition = TOP_MARGIN;
        }
        
        doc.setFillColor(241, 243, 245);
        doc.rect(LEFT_MARGIN - 5, yPosition, CONTENT_WIDTH + 10, 8, "F");
        doc.setFontSize(14);
        doc.setTextColor(30, 58, 138);
        doc.text(dayTitle, LEFT_MARGIN, yPosition + 6);
        yPosition += 12;
        
        // Use jsPDF's built-in text splitting for itinerary content
        const descLines = doc.splitTextToSize(description, CONTENT_WIDTH);
        doc.setFontSize(11);
        doc.setTextColor(50, 50, 50);
        
        for (let i = 0; i < descLines.length; i++) {
          if (yPosition + 6 > BOTTOM_MARGIN) {
            doc.addPage();
            yPosition = TOP_MARGIN;
            // Re-add the day title on new page
            doc.setFillColor(241, 243, 245);
            doc.rect(LEFT_MARGIN - 5, yPosition, CONTENT_WIDTH + 10, 8, "F");
            doc.setFontSize(14);
            doc.setTextColor(30, 58, 138);
            doc.text(dayTitle + " (continued)", LEFT_MARGIN, yPosition + 6);
            yPosition += 12;
            doc.setFontSize(11);
            doc.setTextColor(50, 50, 50);
          }
          doc.text(descLines[i], LEFT_MARGIN, yPosition);
          yPosition += 6;
        }
        yPosition += 10;
      });
    }

    // INCLUSIONS
    doc.addPage();
    yPosition = TOP_MARGIN;
    doc.setFontSize(16);
    doc.setTextColor(30, 58, 138);
    doc.setFont("helvetica", "bold");
    doc.text("INCLUSIONS", LEFT_MARGIN, yPosition);
    yPosition += 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    if (packageDetails.includes && Array.isArray(packageDetails.includes) && packageDetails.includes.length > 0) {
      packageDetails.includes.forEach((item) => {
        // Use jsPDF's built-in text splitting for inclusions
        const lines = doc.splitTextToSize(item, CONTENT_WIDTH - 10);
        for (let i = 0; i < lines.length; i++) {
          if (yPosition + 6 > BOTTOM_MARGIN) {
            doc.addPage();
            yPosition = TOP_MARGIN;
            doc.setFontSize(12);
          }
          doc.text(`${i === 0 ? "• " : "  "}${lines[i]}`, LEFT_MARGIN + 5, yPosition);
          yPosition += 6;
        }
        yPosition += 1;
      });
    } else {
      if (yPosition + 6 > BOTTOM_MARGIN) {
        doc.addPage();
        yPosition = TOP_MARGIN;
      }
      doc.text("No inclusions listed.", LEFT_MARGIN + 5, yPosition);
      yPosition += 7;
    }
    yPosition += 10;

    // EXCLUSIONS
    doc.addPage();
    yPosition = TOP_MARGIN;
    doc.setFontSize(16);
    doc.setTextColor(30, 58, 138);
    doc.setFont("helvetica", "bold");
    doc.text("EXCLUSIONS", LEFT_MARGIN, yPosition);
    yPosition += 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    if (packageDetails.excludes && Array.isArray(packageDetails.excludes) && packageDetails.excludes.length > 0) {
      packageDetails.excludes.forEach((item) => {
        // Use jsPDF's built-in text splitting for exclusions
        const lines = doc.splitTextToSize(item, CONTENT_WIDTH - 10);
        for (let i = 0; i < lines.length; i++) {
          if (yPosition + 6 > BOTTOM_MARGIN) {
            doc.addPage();
            yPosition = TOP_MARGIN;
            doc.setFontSize(12);
          }
          doc.text(`${i === 0 ? "• " : "  "}${lines[i]}`, LEFT_MARGIN + 5, yPosition);
          yPosition += 6;
        }
        yPosition += 1;
      });
    } else {
      if (yPosition + 6 > BOTTOM_MARGIN) {
        doc.addPage();
        yPosition = TOP_MARGIN;
      }
      doc.text("No exclusions listed.", LEFT_MARGIN + 5, yPosition);
      yPosition += 7;
    }

    // TERMS & CONDITIONS
    if (packageDetails.termsAndConditions) {
      doc.addPage();
      yPosition = TOP_MARGIN;
      doc.setFontSize(16);
      doc.setTextColor(30, 58, 138);
      doc.setFont("helvetica", "bold");
      doc.text("TERMS & CONDITIONS", 105, yPosition, { align: "center" });
      yPosition += 15;
      doc.setFontSize(11);
      doc.setTextColor(80, 80, 80);
      doc.setFont("helvetica", "normal");
      
      // Use jsPDF's built-in text splitting for terms
      const termsLines = doc.splitTextToSize(packageDetails.termsAndConditions, CONTENT_WIDTH);
      for (let i = 0; i < termsLines.length; i++) {
        if (yPosition + 6 > BOTTOM_MARGIN) {
          doc.addPage();
          yPosition = TOP_MARGIN;
        }
        doc.text(termsLines[i], LEFT_MARGIN, yPosition);
        yPosition += 6;
      }
    }

    // FOOTER
    const pageCount = doc.internal.getNumberOfPages();
    const footerY = 287;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(LEFT_MARGIN, footerY - 5, PAGE_WIDTH - LEFT_MARGIN, footerY - 5);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Page ${i} of ${pageCount}`, 105, footerY, { align: "center" });
      doc.text("Pearl Tours & Travels", LEFT_MARGIN, footerY);
      doc.text(new Date().toLocaleDateString(), PAGE_WIDTH - LEFT_MARGIN, footerY, { align: "right" });
    }

    doc.save(`${packageDetails.name.replace(/\s+/g, "_")}_package_details.pdf`);
  };
  
  const PackageDetailsModal = ({ pkg, onClose }) => {
    if (!pkg) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1000,
        overflow: 'auto', padding: '20px'
      }} onClick={onClose}>
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', maxWidth: '900px', width: '100%', maxHeight: '90vh',
          overflow: 'auto', position: 'relative'
        }} onClick={(e) => e.stopPropagation()}>
          <button 
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'rgba(0, 0, 0, 0.05)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              fontSize: '24px',
              cursor: 'pointer',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              color: '#666',
              lineHeight: '1',
              outline: 'none',
              padding: 0,
              margin: 0,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#64192E';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'rotate(90deg)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
              e.currentTarget.style.color = '#666';
              e.currentTarget.style.transform = 'rotate(0deg)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'rotate(90deg) scale(0.95)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'rotate(90deg)';
            }}
          >
            ×
          </button>
          
          {pkg.image && (
            <div style={{ height: '300px', overflow: 'hidden' }}>
              <img 
                src={pkg.image} alt={pkg.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          )}
          
          <div style={{padding: '30px'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h1 style={{
                  fontSize: '28px', fontWeight: '700', color: '#2c3e50', margin: '0 0 10px 0'
                }}>{pkg.name}</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', color: '#7f8c8d', fontSize: '16px' }}>
                    <span style={{marginRight: '5px'}}>📍</span> {pkg.destination}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', color: '##7f8c8d', fontSize: '16px' }}>
                    <span style={{marginRight: '5px'}}>⏱️</span> {pkg.duration}
                  </span>
                </div>
              </div>
              
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  color: '#1ca152', fontSize: '28px', fontWeight: '700', lineHeight: '1.2'
                }}>{pkg.cardPriceDisplay}</div>
                {pkg.priceType && (
                  <span style={{
                    fontSize: '14px', color: '#7f8c8d', display: 'block', marginTop: '2px'
                  }}>{pkg.priceType}</span>
                )}
              </div>
            </div>
            
            {pkg.bestSelling && (
              <div style={{
                display: 'inline-block',
                background: 'linear-gradient(90deg, #ff7e5f, #feb47b)',
                color: 'white', padding: '5px 15px',
                borderRadius: '20px', fontSize: '14px', fontWeight: '600', marginBottom: '20px'
              }}>BEST SELLING</div>
            )}
            
            <div style={{ marginBottom: '25px' }}>
              <h2 style={{
                fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '15px',
                borderBottom: '2px solid #f1f2f6', paddingBottom: '10px'
              }}>Overview</h2>
              <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#34495e' }}>
                {pkg.description || "No description available."}
              </p>
            </div>
            
            {pkg.priceValues && pkg.priceValues.length > 0 && (
              <div style={{ marginBottom: '25px' }}>
                <h2 style={{
                  fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '15px',
                  borderBottom: '2px solid #f1f2f6', paddingBottom: '10px'
                }}>Price Details</h2>
                <div style={{ display: 'grid', gap: '10px' }}>
                  {pkg.priceValues.map((price, index) => (
                    <div key={index} style={{
                      padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px'
                    }}>
                      <p style={{
                        margin: 0, fontSize: '15px', lineHeight: '1.4', color: '#34495e'
                      }}>{price}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {pkg.itinerary && pkg.itinerary.length > 0 && (
              <div style={{ marginBottom: '25px' }}>
                <h2 style={{
                  fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '15px',
                  borderBottom: '2px solid #f1f2f6', paddingBottom: '10px'
                }}>Itinerary</h2>
                <div style={{ display: 'grid', gap: '15px' }}>
                  {pkg.itinerary.map((day, index) => (
                    <div key={index} style={{
                      padding: '15px', backgroundColor: '##f8f9fa', borderRadius: '8px', borderLeft: '4px solid #3498db'
                    }}>
                      <h3 style={{
                        fontSize: '16px', fontWeight: '600', margin: '0 0 10px 0', color: '#2c3e50'
                      }}>Day {index + 1}</h3>
                      <p style={{
                        margin: 0, fontSize: '15px', lineHeight: '1.5', color: '#34495e'
                      }}>{day}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{
              display: 'flex', justifyContent: 'center', gap: '20px', margin: '30px 0'
            }}>
              <button style={{
                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                color: 'white', border: 'none', padding: '15px 40px',
                borderRadius: '30px', fontSize: '16px', fontWeight: '600', cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(106, 17, 203, 0.3)'
              }}
                onClick={generatePDF}
              >
                Generate PDF
              </button>
              <button style={{
                background: "linear-gradient(135deg, #f59e42 0%, #f97316 100%)",
                color: "white",
                border: "none",
                borderRadius: "30px", 
                padding: "15px 40px", 
                fontSize: "16px", 
                fontWeight: "600", 
                cursor: "pointer",
                boxShadow: '0 4px 15px rgba(245, 158, 66, 0.3)'
              }}
                onClick={() => handleEnquireNow(pkg)}
              >
                Enquire Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

//   const EnquiryModal = () => {
//     return (
//       <div style={{
//         position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
//         backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex',
//         justifyContent: 'center', alignItems: 'center', zIndex: 1000,
//         overflow: 'auto', padding: '20px'
//       }} onClick={closeEnquiryModal}>
//         <div style={{
//           backgroundColor: 'white', borderRadius: '12px', maxWidth: '800px', width: '100%', maxHeight: '90vh',
//           overflow: 'auto', position: 'relative'
//         }} onClick={(e) => e.stopPropagation()}>
//           <button 
//             onClick={closeEnquiryModal}
//             style={{
//               position: 'absolute', top: '15px', right: '15px', background: 'rgba(0,0,0,0.1)', border: 'none',
//               borderRadius: '50%', width: '40px', height: '40px', fontSize: '20px', cursor: 'pointer', zIndex: 10
//             }}
//           >×</button>
          
//           <div style={{padding: '30px'}}>
//             {submitted ? (
//               <>
//                 <h1 style={{ color: '#2c3e50', marginBottom: '20px', textAlign: 'center' }}>Thank You!</h1>
//                 <p style={{ fontSize: '18px', color: '#7f8c8d', marginBottom: '30px', textAlign: 'center' }}>
//                   Your enquiry has been submitted successfully. We'll get back to you shortly.
//                 </p>
//                 <div style={{ textAlign: 'center' }}>
//                   <button 
//                     onClick={closeEnquiryModal}
//                     style={{
//                       background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
//                       color: 'white', border: 'none', padding: '12px 30px',
//                       borderRadius: '30px', fontSize: '16px', fontWeight: '600', cursor: 'pointer'
//                     }}
//                   >
//                     Close
//                   </button>
//                 </div>
//               </>
//             ) : (
//               <>
//                 <h1 style={{ color: '#2c3e50', marginBottom: '10px' }}>Enquire About Package</h1>
//                 {enquiryFormData.packageName && (
//                   <p style={{ color: '#7f8c8d', marginBottom: '30px' }}>
//                     You're enquiring about: <strong>{enquiryFormData.packageName}</strong>
//                   </p>
//                 )}
                
//                 <form onSubmit={handleEnquirySubmit} style={{ display: 'grid', gap: '20px' }}>
//                   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Full Name *
//                       </label>
//                      <input
//                         type="text" ref={el => inputRefs.current.name = el} 
//                         name="name"
//                         value={enquiryFormData.name}
//                         onChange={handleEnquiryChange}
//                         required
//                         style={{
//                             width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                             fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                     />


//                     </div>
                    
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Email Address *
//                       </label>
//                       <input
//                         type="email"
//                         name="email"
//                         value={enquiryFormData.email}
//                         onChange={handleEnquiryChange}
//                         required
//                         style={{
//                           width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                           fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                       />
//                     </div>
//                   </div>

//                   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Phone Number *
//                       </label>
//                       <input
//                         type="tel"
//                         name="phone"
//                         value={enquiryFormData.phone}
//                         onChange={handleEnquiryChange}
//                         required
//                         style={{
//                           width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                           fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                       />
//                     </div>
                    
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Emergency Contact
//                       </label>
//                       <input
//                         type="text"
//                         name="emergencyContact"
//                         value={enquiryFormData.emergencyContact}
//                         onChange={handleEnquiryChange}
//                         style={{
//                           width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                           fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                         placeholder="Emergency contact number"
//                       />
//                     </div>
//                   </div>

//                   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Number of Adults
//                       </label>
//                       <select
//                         name="adults"
//                         value={enquiryFormData.adults}
//                         onChange={handleEnquiryChange}
//                         style={{
//                           width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                           fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                       >
//                         {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
//                           <option key={num} value={num}>{num}</option>
//                         ))}
//                       </select>
//                     </div>
                    
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Number of Children
//                       </label>
//                       <select
//                         name="children"
//                         value={enquiryFormData.children}
//                         onChange={handleEnquiryChange}
//                         style={{
//                           width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                           fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                       >
//                         {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(num => (
//                           <option key={num} value={num}>{num}</option>
//                         ))}
//                       </select>
//                     </div>
//                   </div>

//                   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Preferred Travel Date
//                       </label>
//                       <input
//                         type="date"
//                         name="travelDate"
//                         value={enquiryFormData.travelDate}
//                         onChange={handleEnquiryChange}
//                         style={{
//                           width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                           fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                       />
//                     </div>
                    
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Departure City
//                       </label>
//                       <input
//                         type="text"
//                         name="departureCity"
//                         value={enquiryFormData.departureCity}
//                         onChange={handleEnquiryChange}
//                         style={{
//                           width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                           fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                         placeholder="Your departure city"
//                       />
//                     </div>
//                   </div>

//                   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Hotel Preference
//                       </label>
//                       <select
//                         name="hotelPreference"
//                         value={enquiryFormData.hotelPreference}
//                         onChange={handleEnquiryChange}
//                         style={{
//                           width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                           fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                       >
//                         <option value="">Select preference</option>
//                         <option value="3 Star">3 Star</option>
//                         <option value="4 Star">4 Star</option>
//                         <option value="5 Star">5 Star</option>
//                         <option value="Luxury">Luxury</option>
//                         <option value="Budget">Budget</option>
//                       </select>
//                     </div>
                    
//                     <div>
//                       <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                         Budget (per person)
//                       </label>
//                       <select
//                         name="budget"
//                         value={enquiryFormData.budget}
//                         onChange={handleEnquiryChange}
//                         style={{
//                           width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                           fontSize: '16px', boxSizing: 'border-box'
//                         }}
//                       >
//                         <option value="">Select budget range</option>
//                         <option value="Under ₹20,000">Under ₹20,000</option>
//                         <option value="₹20,000 - ₹40,000">₹20,000 - ₹40,000</option>
//                         <option value="₹40,000 - ₹60,000">₹40,000 - ₹60,000</option>
//                         <option value="₹60,000 - ₹80,000">₹60,000 - ₹80,000</option>
//                         <option value="Above ₹80,000">Above ₹80,000</option>
//                       </select>
//                     </div>
//                   </div>

//                   <div>
//                     <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                       How did you hear about us?
//                     </label>
//                     <select
//                       name="howDidYouHear"
//                       value={enquiryFormData.howDidYouHear}
//                       onChange={handleEnquiryChange}
//                       style={{
//                         width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                         fontSize: '16px', boxSizing: 'border-box'
//                       }}
//                     >
//                       <option value="">Select option</option>
//                       <option value="Google Search">Google Search</option>
//                       <option value="Social Media">Social Media</option>
//                       <option value="Friend/Relative">Friend/Relative</option>
//                       <option value="Advertisement">Advertisement</option>
//                       <option value="Other">Other</option>
//                     </select>
//                   </div>

//                   <div>
//                     <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                       Special Requirements
//                     </label>
//                     <textarea
//                       name="specialRequirements"
//                       value={enquiryFormData.specialRequirements}
//                       onChange={handleEnquiryChange}
//                       rows="3"
//                       style={{
//                         width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                         fontSize: '16px', boxSizing: 'border-box', resize: 'vertical'
//                       }}
//                       placeholder="Any dietary restrictions, accessibility needs, or other special requirements"
//                     />
//                   </div>
                  
//                   <div>
//                     <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
//                       Message
//                     </label>
//                     <textarea
//                       name="message"
//                       value={enquiryFormData.message}
//                       onChange={handleEnquiryChange}
//                       rows="5"
//                       style={{
//                         width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px',
//                         fontSize: '16px', boxSizing: 'border-box', resize: 'vertical'
//                       }}
//                       placeholder="Tell us more about your travel preferences and expectations"
//                     />
//                   </div>
                  
//                   <button
//                     type="submit"
//                     disabled={submitting}
//                     style={{
//                       background: submitting ? '#ccc' : 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
//                       color: 'white', border: 'none', padding: '15px', borderRadius: '6px',
//                       fontSize: '16px', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer'
//                     }}
//                   >
//                     {submitting ? 'Submitting...' : 'Submit Enquiry'}
//                   </button>
//                 </form>
//               </>
//             )}
//           </div>
//         </div>
//       </div>
//     );
//   };

  if (loading) {
    return (
      <div style={{
  display: 'flex', 
  justifyContent: 'center', 
  alignItems: 'center', 
  height: '100vh', 
  flexDirection: 'column',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
}}>
  {/* Logo with rotation and pulse animation */}
  <div style={{
    width: '100px',
    height: '100px',
    marginBottom: '25px',
    animation: 'rotateAndPulse 2s infinite ease-in-out',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    {/* Company logo image */}
    <img src={logo} style={{
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      objectFit: 'contain',
      boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
    }} alt="Company Logo" />
  </div>
  
  {/* Progress bar */}
  <div style={{
    width: '220px',
    height: '6px',
    backgroundColor: 'rgba(255,255,255,0.3',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '25px'
  }}>
    <div style={{
      width: '100%',
      height: '100%',
      backgroundColor: 'white',
      borderRadius: '3px',
      animation: 'progress 2s infinite ease-in-out'
    }}></div>
  </div>
  
  <p style={{ 
    color: 'white', 
    fontSize: '18px', 
    fontWeight: '500',
    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
    marginBottom: '10px'
  }}>Loading packages...</p>
  
  <p style={{ 
    color: 'rgba(255,255,255,0.8)', 
    fontSize: '14px',
    fontStyle: 'italic'
  }}>Almost there</p>
  
  <style>{`
    @keyframes rotateAndPulse {
      0% { transform: rotate(0deg) scale(1); }
      50% { transform: rotate(180deg) scale(1.1); }
      100% { transform: rotate(360deg) scale(1); }
    }
    @keyframes progress {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `}</style>
</div>
    );
  }

  return (
    <>

        <SEO
        title="All Packages | Pearl Tours & Travels"
        description="Browse all travel packages including international, domestic, and group tours with Pearl Tours & Travels."
        keywords="all tour packages, Pearl Tours packages, domestic tours, international tours, group travel deals"
      />
      <Navbar />
      <div style={{
        maxWidth: '1280px', margin: '0 auto', padding: '40px 20px',
        fontFamily: "'Poppins', sans-serif", backgroundColor: '#f8f9fa', position: 'relative'
      }}>
        <button 
          onClick={() => navigate('/')}
          style={{
            position: 'absolute', top: '20px', left: '20px', display: 'flex', alignItems: 'center',
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', color: 'white', border: 'none',
            borderRadius: '30px', padding: '8px 20px 8px 15px', fontSize: '14px', fontWeight: '600',
            cursor: 'pointer', boxShadow: '0 4px 15px rgba(106, 17, 203, 0.3)',
            transition: 'all 0.3s ease', zIndex: 10
          }}
        >
          <span style={{ marginRight: '5px', fontSize: '16px' }}>←</span> Back to Home
        </button>
        <div style={{
          textAlign: 'center', margin: '30px 0 40px 0', paddingTop: '20px'
        }}>
          <h1 style={{
            fontSize: '2.5rem', fontWeight: '700', color: '#2c3e50', marginBottom: '15px',
            background: 'linear-gradient(135deg, ' + (Math.random() > 0.5 ? '#6a11cb 0%, #2575fc 100%' : '#f59e42 0%, #f97316 100%') + ')', WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>Discover Amazing Tours</h1>
          <p style={{
            fontSize: '1.1rem', color: '#7f8c8d', maxWidth: '700px', margin: '0 auto', lineHeight: '1.6'
          }}>Explore our handpicked selection of bestselling tour packages around the world</p>
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: '25px', marginBottom: '40px'
        }}>
          {tours.slice(0, visibleTours).map((tour) => (
            <div key={tour.id}
              onClick={() => handlePackageClick(tour)}
              style={{
                background: '#fff', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 5px 20px rgba(0,0,0,0.1)', transition: 'all 0.3s ease', cursor: 'pointer', position: 'relative'
              }}
            >
              <div style={{position: 'relative', overflow: 'hidden'}}>
                {tour.image ? (
                  <img 
                    style={{
                      width: '100%', height: '200px', objectFit: 'cover',
                      transition: 'transform 0.5s ease'
                    }}
                    src={tour.image} 
                    alt={tour.title || tour.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/300x200/3498db/ffffff?text=No+Image";
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%', height: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center',
                    backgroundColor: '#ecf0f1', color: '#7f8c8d', fontSize: '14px'
                  }}>No Image Available</div>
                )}
                {tour.bestSelling && (
                  <div style={{
                    position: 'absolute', top: '15px', left: '0',
                    background: 'linear-gradient(90deg, #ff7e5f, #feb47b)', color: 'white',
                    padding: '5px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '0 4px 4px 0',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                  }}>BEST SELLING</div>
                )}
                <div style={{
                  position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(0,0,0,0.7)',
                    color: 'white', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500'
                }}>{tour.duration}</div>
              </div>
              <div style={{padding: '20px'}}>
                <div style={{
                  fontSize: '18px', fontWeight: '700', color: '#2c3e50', marginBottom: '10px',
                  lineHeight: '1.3', height: '46px', overflow: 'hidden',
                  textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical'
                }}>{tour.name}</div>
                <div style={{
                  display: 'flex', alignItems: 'center', marginBottom: '12px'
                }}>
                  <div style={{
                    color: '#f39c12', fontSize: '14px', marginRight: '5px'
                  }}>{"★".repeat(5)}</div>
                  <div style={{
                    fontSize: '13px', color: '##7f8c8d'
                  }}>(5.0)</div>
                </div>
                <div style={{
                  fontSize: '14px', color: '#7f8c8d', lineHeight: '1.5', marginBottom: '15px',
                  height: '66px', overflow: 'hidden'
                }}>
                  {tour.description && tour.description.length > 80 
                    ? `${tour.description.substring(0, 80)}... ` 
                    : tour.description || "Explore amazing destinations with our expertly crafted tour package."}
                </div>
                
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px',
                  paddingTop: '15px', borderTop: '1px solid #eee'
                }}>
                  <div>
                    <div style={{
                      color: '#1ca152', fontSize: '20px', fontWeight: '700', lineHeight: '1.2'
                    }}>{tour.cardPriceDisplay}</div>
                    {tour.priceType && (
                      <span style={{
                        fontSize: '12px', color: '#7f8c8d', display: 'block', marginTop: '2px'
                      }}>{tour.priceType}</span>
                    )}
                  </div>
                  <button 
                    style={{
                      background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                      color: 'white', border: 'none', padding: '8px 20px', borderRadius: '30px',
                      fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {tours.length > 8 && (
          <div style={{textAlign: 'center', marginTop: '20px'}}>    
            <button
              style={{
                background: 'transparent', border: '2px solid #3498db', color: '#3498db',
                padding: '12px 30px', borderRadius: '30px', fontSize: '16px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.3s ease', outline: 'none'
              }}
              onClick={toggleToursVisibility}
            >
              {showAllTours ? "Show Less Packages" : "View All Packages"}
            </button>
          </div>
        )}
        {/* <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px',
          marginTop: '60px', padding: '30px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px', color: 'white'
         }}>
          <div style={{textAlign: 'center'}}>
            <div style={{fontSize: '40px', marginBottom: '10px'}}>🌍</div>
            <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>Worldwide Tours</h3>
            <p style={{margin: '0', fontSize: '14px', opacity: '0.9'}}>Explore destinations across the globe</p>
          </div>
          <div style={{textAlign: 'center'}}>
            <div style={{fontSize: '40px', marginBottom: '10px'}}>⭐</div>
            <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>Best Value</h3>
            <p style={{margin: '0', fontSize: '14px', opacity: '0.9'}}>Quality experiences at great prices</p>
          </div>
          <div style={{textAlign: 'center'}}>
            <div style={{fontSize: '40px', marginBottom: '10px'}}>🛡️</div>
            <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>24/7 Support</h3>
            <p style={{margin: '0', fontSize: '14px', opacity: '0.9'}}>Help whenever you need it</p>
          </div>
          <div style={{textAlign: 'center'}}>
            <div style={{fontSize: '40px', marginBottom: '10px'}}>✈️</div>
            <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>Easy Booking</h3>
            <p style={{margin: '0', fontSize: '14px', opacity: '0.9'}}>Simple and secure reservation process</p>
          </div>
        </div> */}

        <div style={{
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '20px',
  marginTop: '60px',
  padding: '30px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '12px',
  color: 'white',
  marginBottom: '40px'
}}>
  <div style={{textAlign: 'center'}}>
    <div style={{fontSize: '40px', marginBottom: '10px'}}>🌍</div>
    <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>Worldwide Tours</h3>
    <p style={{margin: '0', fontSize: '14px', opacity: '0.9'}}>Explore destinations across the globe</p>
  </div>
  <div style={{textAlign: 'center'}}>
    <div style={{fontSize: '40px', marginBottom: '10px'}}>⭐</div>
    <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>Best Value</h3>
    <p style={{margin: '0', fontSize: '14px', opacity: '0.9'}}>Quality experiences at great prices</p>
  </div>
  <div style={{textAlign: 'center'}}>
    <div style={{fontSize: '40px', marginBottom: '10px'}}>🛡️</div>
    <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>24/7 Support</h3>
    <p style={{margin: '0', fontSize: '14px', opacity: '0.9'}}>Help whenever you need it</p>
  </div>
  <div style={{textAlign: 'center'}}>
    <div style={{fontSize: '40px', marginBottom: '10px'}}>✈️</div>
    <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>Easy Booking</h3>
    <p style={{margin: '0', fontSize: '14px', opacity: '0.9'}}>Simple and secure reservation process</p>
  </div>
</div>
        
        
      </div>
      <Footer />
      {showModal && (
        <PackageDetailsModal 
          pkg={selectedPackage} 
          onClose={closeModal} 
        />
      )}
      {showEnquiryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          {/* Modal content */}
        </div>
      )}
      <CTA packageDetails={{
        packageName: enquiryFormData.packageName,
        destination: enquiryFormData.destination,
        duration: enquiryFormData.duration
      }} />
      
    </>
  );
};

export default AllPackages;